import { kuunda } from '@/lib/kuunda';
import {
  captureUtmFromLocation,
  detectDeviceType,
  getConsent,
  getOrCreateSessionId,
  getOrCreateVisitorId,
  getStoredUtm,
  touchSession,
  type StoredUtm,
} from '@/features/analytics/cookies';

export type AnalyticsEventType = 'pageview' | 'heartbeat' | 'exit' | 'conversion' | 'engage';

type TrackPayload = {
  eventType: AnalyticsEventType;
  path: string;
  title?: string;
  referrer?: string;
  durationMs?: number;
  meta?: Record<string, unknown>;
  search?: string;
};

const SKIP_PREFIXES = ['/admin', '/auth/callback', '/dev'];

let lastPageEnteredAt = 0;
let lastPath: string | null = null;
let queue: Promise<unknown> = Promise.resolve();

function shouldSkipPath(path: string): boolean {
  return SKIP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

async function send(payload: TrackPayload) {
  if (getConsent() !== 'accepted') return;
  if (shouldSkipPath(payload.path)) return;

  const visitorId = getOrCreateVisitorId();
  const sessionId = getOrCreateSessionId();
  if (!visitorId || !sessionId) return;

  touchSession();
  if (payload.search) captureUtmFromLocation(payload.search);
  const utm: StoredUtm = getStoredUtm();

  const { error } = await kuunda.rpc('track_analytics_event', {
    p_visitor_id: visitorId,
    p_session_id: sessionId,
    p_event_type: payload.eventType,
    p_path: payload.path.slice(0, 500),
    p_title: (payload.title || (typeof document !== 'undefined' ? document.title : '') || '').slice(0, 300) || null,
    p_referrer: ((payload.referrer ?? (typeof document !== 'undefined' ? document.referrer : '')) || '').slice(0, 1000) || null,
    p_utm_source: utm.utm_source ?? null,
    p_utm_medium: utm.utm_medium ?? null,
    p_utm_campaign: utm.utm_campaign ?? null,
    p_utm_content: utm.utm_content ?? null,
    p_utm_term: utm.utm_term ?? null,
    p_locale: typeof navigator !== 'undefined' ? navigator.language?.slice(0, 20) : null,
    p_device_type: detectDeviceType(),
    p_screen_w: typeof window !== 'undefined' ? window.screen?.width : null,
    p_screen_h: typeof window !== 'undefined' ? window.screen?.height : null,
    p_duration_ms: payload.durationMs ?? null,
    p_meta: payload.meta ?? {},
  });

  if (error && import.meta.env.DEV) {
    console.warn('[analytics]', error.message);
  }
}

function enqueue(payload: TrackPayload) {
  queue = queue.then(() => send(payload)).catch(() => undefined);
}

/** Enregistre une page vue et clôture la précédente avec sa durée. */
export function trackPageview(path: string, search = '') {
  if (getConsent() !== 'accepted') return;
  const now = Date.now();
  if (lastPath && lastPageEnteredAt) {
    const durationMs = Math.max(0, Math.min(now - lastPageEnteredAt, 30 * 60 * 1000));
    enqueue({
      eventType: 'exit',
      path: lastPath,
      durationMs,
      search,
    });
  }
  lastPath = path;
  lastPageEnteredAt = now;
  enqueue({
    eventType: 'pageview',
    path,
    search,
    referrer: typeof document !== 'undefined' ? document.referrer : undefined,
  });
}

/** Conversion (inscription, contact, etc.). */
export function trackConversion(kind: string, meta: Record<string, unknown> = {}, path?: string) {
  if (getConsent() !== 'accepted') return;
  enqueue({
    eventType: 'conversion',
    path: path || (typeof location !== 'undefined' ? location.pathname : '/'),
    search: typeof location !== 'undefined' ? location.search : '',
    meta: { kind, ...meta },
  });
}

/** Engagement (CTA cliqué). */
export function trackEngage(kind: string, meta: Record<string, unknown> = {}, path?: string) {
  if (getConsent() !== 'accepted') return;
  enqueue({
    eventType: 'engage',
    path: path || (typeof location !== 'undefined' ? location.pathname : '/'),
    search: typeof location !== 'undefined' ? location.search : '',
    meta: { kind, ...meta },
  });
}

/** Flush durée de la page courante (onglet caché / fermeture). */
export function flushCurrentPage() {
  if (getConsent() !== 'accepted' || !lastPath || !lastPageEnteredAt) return;
  const durationMs = Math.max(0, Math.min(Date.now() - lastPageEnteredAt, 30 * 60 * 1000));
  enqueue({
    eventType: 'heartbeat',
    path: lastPath,
    durationMs,
    search: typeof location !== 'undefined' ? location.search : '',
  });
  lastPageEnteredAt = Date.now();
}
